/**
 * how-we-work-interaction.js
 * 
 * Handles the sticky "hero dock" navigator, scroll-linked progress fills,
 * and mobile auto-hide nav behavior.
 */

export function initHowWeWorkInteraction() {
  'use strict';

  // ── Hardcoded State (Stripped Tweaks) ──
  const state = {
    pattern: "herodock",
    scrollLinked: true,
    hoverPreview: true,
    mobileMode: "autoHide",
    accent: "#E5322D"
  };

  const body = document.body;
  const root = document.documentElement;

  // ── Chapter metadata (Synced with Sanity IDs) ──
  const CHAPTERS = [
    { id: 'sv-what',  num: '1', eyebrow: 'What we do',          preview: 'What we do, partnership across systems, security, AI and quality.' },
    { id: 'sv-start', num: '2', eyebrow: 'How we get started',  preview: 'How we get started, engagement models and a 5-step onboarding.' },
    { id: 'sv-how',   num: '3', eyebrow: 'How we do it',        preview: 'How we do it — Understand · Shape · Plan · Build · Test · Evolve.' },
  ];

  // ── DOM Elements ──
  const heroDock = document.getElementById('hero-dock');
  const sections = CHAPTERS.map(c => document.getElementById(c.id));

  // ── Scroll tracking state ──
  let active = 0;
  let progress = 0;
  let _initial = true;
  let raf = 0;
  let lastY = 0;

  // ── Chapter Progress Rendering ──
  function renderProgress() {
    // 1. Hero dock
    if (heroDock) {
      heroDock.classList.toggle('is-initial', _initial);
      heroDock.querySelectorAll('.hd-seg').forEach((s, i) => {
        s.classList.toggle('is-active', !_initial && i === active);
        s.classList.toggle('is-done', !_initial && i < active);
      });
      
      const hdFill = heroDock.querySelector('.hero-dock-progress > i');
      if (hdFill) {
        let hdPct = 0;
        if (!_initial) { 
          const per = 1 / CHAPTERS.length; 
          hdPct = (active * per + progress * per) * 100; 
        }
        hdFill.style.width = (state.scrollLinked ? hdPct : 0) + '%';
      }
    }
  }

  // ── Scroll Handler ──
  function onScroll() {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const vh = window.innerHeight;
      const anchor = vh * 0.30;
      
      let act = 0;
      sections.forEach((sec, i) => {
        if (!sec) return;
        const r = sec.getBoundingClientRect();
        if (r.top <= anchor) act = i;
      });

      const firstSec = sections[0];
      if (firstSec) {
        _initial = firstSec.getBoundingClientRect().top > anchor;
      }

      const cur = sections[act];
      if (cur) {
        const r = cur.getBoundingClientRect();
        const total = r.height - vh * 0.5;
        const traveled = -r.top + vh * 0.30;
        progress = Math.max(0, Math.min(1, total > 0 ? traveled / total : 0));
      }

      active = act;
      
      const y = window.scrollY;
      const goingDown = y > lastY + 4;
      const goingUp   = y < lastY - 4;

      // Mobile Auto-hide Nav (Trigger only on screen width <= 768px)
      const isMobile = window.innerWidth <= 768;
      if (isMobile && state.mobileMode === 'autoHide' && y > 160) {
        if (goingDown) body.classList.add('nav-hidden');
        else if (goingUp) body.classList.remove('nav-hidden');
      } else {
        body.classList.remove('nav-hidden');
      }

      if (Math.abs(y - lastY) > 4) lastY = y;
      renderProgress();
    });
  }

  // ── Jump to Section ──
  function initJumpLinks() {
    document.addEventListener('click', (e) => {
      const j = e.target.closest('[data-jump]');
      if (!j) return;
      
      const id = j.dataset.jump;
      const el = document.getElementById(id);
      if (!el) return;

      const navH = parseInt(getComputedStyle(root).getPropertyValue('--nav-h')) || 60;
      const offset = state.pattern === 'herodock' ? navH + 68 : navH;
      const top = el.getBoundingClientRect().top + window.scrollY - offset + 2;
      
      window.scrollTo({ top, behavior: 'smooth' });
    });
  }

  // ── Init ──
  const navEl = document.querySelector('.navbar');
  if (navEl) {
    root.style.setProperty('--nav-h', navEl.offsetHeight + 'px');
  }

  function handleNavbarScroll() {
    const navbar = document.querySelector('.navbar');
    if (!navbar) return;

    if (window.scrollY > 50) {
      navbar.classList.add('navbar-v3-scrolled');
      navbar.classList.remove('navbar-v3-transparent');
    } else {
      navbar.classList.remove('navbar-v3-scrolled');
      navbar.classList.add('navbar-v3-transparent');
    }
  }

  window.addEventListener('scroll', () => {
    onScroll();
    handleNavbarScroll();
  }, { passive: true });

  window.addEventListener('resize', () => {
    if (navEl) root.style.setProperty('--nav-h', navEl.offsetHeight + 'px');
    onScroll();
  });

  initJumpLinks();
  onScroll();
  handleNavbarScroll();
}
